import { api } from '@verifime/api-definition';
import { useEffect, useState } from 'react';
import { toDisplayableCapture } from './toDisplayableCapture';

export type TUseIdPortraitEvidenceParams = {
  customerId?: string;
  /**
   * Whether to fetch at all. The card sets this only for `NotCompleted`, the one biometric level
   * with no face-check capture behind it: this is the sole signal the API offers for whether a
   * `VIEW ID PORTRAIT` button has anything to show, so every other level would pay a call it never
   * uses.
   */
  enabled?: boolean;
  /** Called once if the call fails. This package has no logging surface of its own. */
  onError?: (error: unknown) => void;
};

export type TUseIdPortraitEvidence = {
  /** True until the call has settled, whether it succeeded or failed. */
  isLoading: boolean;
  /**
   * A `blob:` object URL, not the endpoint's own presigned S3 URL: the portal's `img-src` policy
   * allows the former and blocks the latter. Falls back to the raw URL, unconverted, when it could
   * not be fetched, so the dialog's existing failure tile still fires. See `toDisplayableCapture`.
   * Absent, without that being an error, when the customer has no ID portrait at all.
   */
  portraitUrl?: string;
};

const NOTHING_GATHERED: TUseIdPortraitEvidence = { isLoading: true };

/**
 * The identity document portrait, fetched once at card level rather than behind the dialog it also
 * feeds.
 *
 * **This is both the gate and the content, deliberately one fetch.** `IdentityPortraitItemResponse`
 * carries no availability field of its own: whether `portraitUrl` came back *is* the only signal for
 * whether a `NotCompleted` customer has a portrait worth a `VIEW ID PORTRAIT` button at all. Calling
 * this hook a second time once the dialog opens, the way `useBiometricEvidence` calls its own four
 * endpoints only when `open`, would fetch the same portrait twice for every customer who clicks
 * through. Instead `CustomerConfidenceCards` calls this hook once, uses `portraitUrl` to decide
 * whether to render the button, and passes that same value straight into `IdPortraitEvidence` as a
 * prop. No separate cache layer is introduced for this: React's own prop threading is the cache, the
 * same way `useFaceCheckTasks`'s `requestState` is computed once and shared between the card and
 * `BiometricEvidence` rather than re-derived by each.
 *
 * **Deliberately not `useBiometricEvidence`.** That hook's fourth call is this same
 * `getV1personIdportrait`, but it only fires once the dialog is open, and the card needs the answer
 * before that: it decides whether to show the `VIEW ID PORTRAIT` button at all, which has to be
 * known before the dialog exists to open. Gating on `open` is exactly the timing this hook exists to
 * avoid, not a difference in what the two hooks fetch.
 */
export function useIdPortraitEvidence({
  customerId,
  enabled = true,
  onError,
}: TUseIdPortraitEvidenceParams): TUseIdPortraitEvidence {
  const [evidence, setEvidence] = useState<TUseIdPortraitEvidence>(NOTHING_GATHERED);

  useEffect(() => {
    // Reset before anything else, including on the early return. Without this a customer whose call
    // fails after a previous customer's succeeded would keep showing the previous customer's
    // portrait under the new customer's name.
    setEvidence(NOTHING_GATHERED);

    if (!customerId || !enabled) {
      return;
    }

    let abandoned = false;
    // The object URL this run creates, so the cleanup below can revoke it. Stays a no-op when there
    // was nothing to convert or the fetch fell back to the raw URL: see `toDisplayableCapture`.
    let release = () => {};

    api
      .getV1personIdportrait({ params: { id: customerId } })
      .then((response) => toDisplayableCapture((response ?? {}).portraitUrl))
      .then((displayable) => {
        if (abandoned) {
          // The customer changed, or this ran on an unmounted component, while the fetch was in
          // flight. Nothing should hold the blob open for a portrait nobody is displaying.
          displayable.release();
          return;
        }
        release = displayable.release;
        setEvidence({ isLoading: false, portraitUrl: displayable.src });
      })
      .catch((error) => {
        if (!abandoned) {
          setEvidence({ isLoading: false });
          onError?.(error);
        }
      });

    return () => {
      abandoned = true;
      release();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, enabled]);

  return evidence;
}
